import { XIcon } from '@icons/index.js';
import { UIButton, UIDiv, UIElement, UIText } from '@ui';

export default class Modal {
  constructor(title = '', options = {}) {
    // this.noBg = false;
    this.title = title;
    this.options = {
      handleOnAction: options.handleOnAction || (() => {}),
      handleOnCancel: options.handleOnCancel || (() => {}),
      description: options.description || '',
      cancelText: options.cancelText || i18next.t('common.cancel'),
      confirmText: options.confirmText || i18next.t('common.confirm'),

      showCancel: options.showCancel !== undefined ? options.showCancel : true,
      showConfirm:
        options.showConfirm !== undefined ? options.showConfirm : true,
      showCloseIcon:
        options.showCloseIcon !== undefined ? options.showCloseIcon : false,
      cancelClass: `px-4 py-2 min-w-[80px] bg-[var(--o-hive-light-100)] rounded-lg cursor-pointer hover:bg-gray-300 transition-colors duration-200 ${options.cancelClass || ''}`,
      confirmClass: `px-4 py-2 min-w-[80px] bg-[var(--o-hive-primary-100)] text-white rounded-lg cursor-pointer hover:enabled:bg-[var(--o-hive-primary-60)] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${options.confirmClass || ''}`,

      content: options.content || null,
      modalContentClass: options.modalContentClass || '',
      contentContainerClass: options.contentContainerClass || '',
      canClose: options.canClose !== undefined ? options.canClose : true,

      noBg: options.noBg || false,
    };

    this.contentContainer;
    this.buttonContainer;
    this.actionButton;
    this.dialog = new UIDiv().setClass(
      'modal fixed inset-0 bg-black bg-opacity-50 backdrop-blur-[3px] flex items-center justify-center overflow-x-hidden p-4 opacity-0 transition-all duration-300 ease-out',
    );
    this.resolvePromise = null;

    // track mouse down position to prevent closing on text selection
    this.mouseDownTarget = null;

    if (!this.options.noBg) {
      this.dialog.dom.addEventListener('mousedown', e => {
        this.mouseDownTarget = e.target;
      });

      this.dialog.onClick(e => {
        if (typeof this.options.handleOutClick === 'function') {
          this.options.handleOutClick(e, () => this.close(null));
        } else if (
          this.options.canClose &&
          e.target === this.dialog.dom &&
          e.currentTarget === this.dialog.dom &&
          this.mouseDownTarget === this.dialog.dom
        ) {
          this.close(null);
        }
        this.mouseDownTarget = null;
      });
    } else {
      this.dialog.addClass('empty-task');
    }
  }

  createModal() {
    this.dialog.setStyle('display', ['block']);
    const modalContent = new UIDiv().setClass(
      `modal-content bg-white rounded-lg shadow-xl gap-0 w-full max-w-xl p-6 transform -translate-y-1/2 scale-95 opacity-0 transition-all duration-300 ease-out overflow-hidden ${this.options.modalContentClass}`,
    );

    const titleContainer = new UIDiv().setClass(
      'flex flex-col gap-1.5 justify-between items-center mb-4',
    );
    if (this.title) {
      const titleText = new UIText(this.title).setClass(
        'text-xl font-medium text-center',
      );
      titleContainer.add(titleText);
    }
    if (this.options.description) {
      const descriptionText = new UIText(this.options.description).setClass(
        `text-center text-gray-600 ${this.title ? 'text-sm' : 'text-lg'}`,
      );
      titleContainer.add(descriptionText);
    }

    if (this.options.showCloseIcon) {
      const closeButton = new XIcon({ size: 15, color: 'black' })
        .setClass(
          'absolute top-4 right-4 text-2xl font-bold cursor-pointer hover:text-gray-500',
        )
        .onClick(() => {
          if (this.options.canClose) {
            this.close(null);
          }
        });
      modalContent.add(closeButton);
    }

    this.contentContainer = new UIDiv().setClass(
      'md:max-h-[70vh] overflow-auto w-full',
    );
    if (this.options.contentContainerClass) {
      this.contentContainer.addClass(this.options.contentContainerClass);
    }

    if (this.options.content) {
      if (this.options.content instanceof UIElement) {
        this.contentContainer.add(this.options.content);
      } else if (typeof this.options.content === 'string') {
        const contentText = new UIText(this.options.content);
        contentText.setClass('text-center py-2');
        this.contentContainer.add(contentText);
      }
    }

    modalContent.add(titleContainer, this.contentContainer);
    if (this.options.showCancel || this.options.showConfirm) {
      this.buttonContainer = new UIDiv().setClass(
        'flex justify-evenly w-full space-x-2 mt-6',
      );

      if (this.options.showCancel) {
        this.cancelButton = new UIButton(this.options.cancelText)
          .setClass(this.options.cancelClass)
          .onClick(() => this.onClose());
        this.buttonContainer.add(this.cancelButton);
      }

      if (this.options.showConfirm) {
        this.actionButton = new UIButton(this.options.confirmText)
          .setClass(this.options.confirmClass)
          .onClick(() => this.onAction());
        this.buttonContainer.add(this.actionButton);
      }

      modalContent.add(this.buttonContainer);
    }

    this.setContent(modalContent);

    this.dialog.setStyle('opacity', ['0']);
    modalContent.dom.style.transform = 'translate(0, -50%) scale(0.95)';
    modalContent.dom.style.opacity = '0';
    modalContent.dom.style.maxHeight = '-webkit-fill-available';
    requestAnimationFrame(() => {
      this.dialog.removeClass('hidden');
      this.dialog.setStyle('opacity', ['1']);
      modalContent.dom.style.transform = 'translate(0, -50%) scale(1)';
      modalContent.dom.style.opacity = '1';
    });

    return this.dialog;
  }

  setContent(component) {
    this.dialog.clear();
    if (component instanceof UIElement) {
      this.dialog.add(component);
    } else {
      this.dialog.dom.appendChild(component);
    }
  }

  show(elemId) {
    return new Promise(resolve => {
      this.resolvePromise = resolve;
      if (elemId) {
        document.querySelector(elemId).appendChild(this.createModal().dom);
      } else {
        document.body.appendChild(this.createModal().dom);
      }
    });
  }

  close(result) {
    this.dialog.setStyle('opacity', ['0']);
    const modalContent = this.dialog.dom.querySelector('.modal-content');
    if (modalContent) {
      modalContent.style.opacity = '0';
      modalContent.style.transform = 'translate(0, -50%) scale(0.95)';
    }
    setTimeout(() => {
      if (this.dialog.dom.parentNode) {
        this.dialog.dom.parentNode.removeChild(this.dialog.dom);
      }
      if (this.resolvePromise) {
        this.resolvePromise(result);
      }
    }, 300);
  }

  onClose() {
    if (!this.options.handleOnCancel) {
      this.close(null);
      return;
    }
    try {
      const result = this.options.handleOnCancel();
      if (result !== false) {
        this.close(true);
      }
    } catch (error) {
      console.error('Error handling onClose:', error);
    }
  }

  async onAction() {
    try {
      const result = await this.options.handleOnAction();
      if (result !== false) {
        this.close(true);
      }
    } catch (error) {
      // Don't close modal if there's an error
      console.error('Modal action failed:', error);
    }
  }

  disabledActionButton(bool = true) {
    if (!this.actionButton) return;
    this.actionButton.setDisabled(bool);
  }
}
